/**
 * Video Element Registry
 * 
 * Global registry for video element references.
 * Allows external components to control video playback directly.
 * 
 * This is a singleton pattern that maintains a Map of video keys to HTMLVideoElement refs.
 * Components register/unregister their video elements, and external components can
 * access them to control playback.
 */

class VideoElementRegistry {
    private static instance: VideoElementRegistry;
    private videoElements: Map<string, HTMLVideoElement> = new Map();

    private constructor() { }

    static getInstance(): VideoElementRegistry {
        if (!VideoElementRegistry.instance) {
            VideoElementRegistry.instance = new VideoElementRegistry();
        }
        return VideoElementRegistry.instance;
    }

    /**
     * Register a video element
     */
    register(videoKey: string, element: HTMLVideoElement): void {
        this.videoElements.set(videoKey, element);
    }

    /**
     * Unregister a video element
     */
    unregister(videoKey: string): void {
        this.videoElements.delete(videoKey);
    }

    /**
     * Get a video element by key
     */
    get(videoKey: string): HTMLVideoElement | undefined {
        return this.videoElements.get(videoKey);
    }

    /**
     * Get all registered video keys
     */
    getAllVideoKeys(): string[] {
        return Array.from(this.videoElements.keys());
    }

    /**
     * Play a specific video
     */
    async play(videoKey: string): Promise<void> {
        console.log(`▶️ [VideoElementRegistry] play called for videoKey: ${videoKey}`);
        const element = this.videoElements.get(videoKey);
        if (element) {
            try {
                await element.play();
            } catch (err) {
                console.warn(`[VideoRegistry] Failed to play video ${videoKey}:`, err);
            }
        }
    }

    /**
     * Pause a specific video
     */
    pause(videoKey: string): void {
        console.log(`⏸️ [VideoElementRegistry] pause called for videoKey: ${videoKey}`);
        const element = this.videoElements.get(videoKey);
        if (element && !element.paused) {
            element.pause();
        }
    }

    /**
     * Pause all videos
     */
    pauseAll(): void {
        this.videoElements.forEach((element) => {
            if (!element.paused) {
                element.pause();
            }
        });
    }

    /**
     * Seek to a specific time in a video
     */
    seek(videoKey: string, time: number): void {
        const element = this.videoElements.get(videoKey);
        if (element) {
            element.currentTime = time;
        }
    }

    /**
     * Set volume for a specific video
     */
    setVolume(videoKey: string, volume: number): void {
        const element = this.videoElements.get(videoKey);
        if (element) {
            element.volume = Math.max(0, Math.min(1, volume)); // Clamp to 0-1
        }
    }

    /**
     * Set mute state for a specific video
     */
    setMuted(videoKey: string, muted: boolean): void {
        const element = this.videoElements.get(videoKey);
        if (element) {
            element.muted = muted;
        }
    }

    /**
     * Toggle mute state for a specific video
     */
    toggleMute(videoKey: string): void {
        const element = this.videoElements.get(videoKey);
        if (element) {
            element.muted = !element.muted;
        }
    }
}

export const videoElementRegistry = VideoElementRegistry.getInstance();
